import * as React from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { useSitesPageStore } from "./sitesPageStore/sitesPageStore";
import { useAppStore } from "../../../../appStore";
import {
  downloadSitesOverview,
  SitesOverviewFailedError,
  SitesOverviewProgressHandler,
} from "./SitesPageUtils/downloadSitesOverview";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TALK_DOWN_FLAG } from "../../../common/TalkDown/useTalkDownEnabled";

const ITEM_HEIGHT = 48;

export const AddGroupOrSiteDropDownMenu = ({
  navigate,
  setAddGroupDialogOpen,
}: {
  navigate: any;
  setAddGroupDialogOpen: any;
}) => {
  const { userPermissions } = useAppStore();
  const { flags } = useFeatureFlag();
  const {
    setImmixRemotelinkDialogOpen,
    searchGroupId,
    sitesOverviewNotice: overviewNotice,
    setSitesOverviewNotice: setOverviewNotice,
    sitesOverviewInProgress: overviewInProgress,
    setSitesOverviewInProgress: setOverviewInProgress,
  } = useSitesPageStore();
  // Add Group / Add Site / Calendars create things and follow add_cameras
  // (the routes behind them are gated the same way). Download Sites Overview
  // is a read every level may run, View Only included; the API behind it
  // (site_settings_report/sites_csv/) only requires authentication.
  const canAdd = Boolean(userPermissions?.add_cameras);
  const showTalkDownMessages =
    Boolean(flags?.[TALK_DOWN_FLAG]) && Boolean(userPermissions?.talk_down);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // The sites overview is built off-request on the backend and can take a
  // few minutes for a large group, so keep the user informed while we poll.
  // State is in the sites page store so it outlives this component.
  const handleDownloadSitesOverview = () => {
    handleClose();
    if (overviewInProgress) {
      return;
    }
    setOverviewInProgress(true);
    const onProgress: SitesOverviewProgressHandler = (progress, url) => {
      if (progress === "preparing") {
        setOverviewNotice({
          severity: "info",
          message:
            "Preparing the sites overview. This can take a few minutes for a large group; the download starts automatically when it is ready.",
        });
      } else if (url) {
        setOverviewNotice({
          severity: "success",
          message:
            "Sites overview ready. If the download did not start, use Download.",
          url,
        });
      } else {
        setOverviewNotice({
          severity: "success",
          message: "Sites overview ready. Your download has started.",
        });
      }
    };
    downloadSitesOverview(searchGroupId, onProgress)
      .catch((error: unknown) => {
        setOverviewNotice({
          severity: "error",
          message:
            error instanceof SitesOverviewFailedError
              ? error.message
              : "Failed to download the sites overview. Please try again.",
        });
      })
      .finally(() => setOverviewInProgress(false));
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            // No fixed width: auto-size to the longest item so the padding
            // is symmetric (MenuItem's own 16px on each side)
            maxHeight: ITEM_HEIGHT * 6.5,
          },
        }}
      >
        {canAdd && (
          <MenuItem
            key="add-group"
            onClick={() => {
              setAddGroupDialogOpen(true);
              handleClose();
            }}
          >
            Add Group
          </MenuItem>
        )}
        {canAdd && (
          <MenuItem
            key="add-site"
            onClick={() => {
              handleClose();
              localStorage.setItem("clickedFromSiteAboutPage", "false");
              navigate("/sites/addsite");
              localStorage.setItem("addAnotherScheduleScreen", "false");
            }}
          >
            Add Site
          </MenuItem>
        )}
        {canAdd && (
          <MenuItem
            key="calendars"
            onClick={() => {
              handleClose();
              navigate("/calendars");
            }}
          >
            Calendars
          </MenuItem>
        )}
        {showTalkDownMessages && (
          <MenuItem
            key="talk-down-messages"
            onClick={() => {
              handleClose();
              navigate("/talkdown");
            }}
          >
            <span className="inline-flex items-center gap-2 font-mulish">
              Talk Down Messages
              <span className="rounded bg-actuate-orange px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                NEW
              </span>
            </span>
          </MenuItem>
        )}
        <MenuItem
          key="download-sites-overview"
          onClick={handleDownloadSitesOverview}
          disabled={overviewInProgress}
        >
          {overviewInProgress
            ? "Preparing Sites Overview…"
            : "Download Sites Overview"}
        </MenuItem>
        {userPermissions.remote_link && (
          <MenuItem
            key="immix-remotelink"
            onClick={() => {
              handleClose();
              setImmixRemotelinkDialogOpen(true);
            }}
          >
            Immix Remotelink
          </MenuItem>
        )}
      </Menu>
      <Snackbar
        open={overviewNotice !== null}
        // Every notice stays until dismissed: a user who switches away for
        // the minutes a big group takes must still find what happened, and
        // the ready notice carries the click-to-download fallback.
        autoHideDuration={null}
        onClose={(_event, reason) => {
          if (reason === "clickaway") return;
          setOverviewNotice(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={overviewNotice?.severity ?? "info"}
          onClose={() => setOverviewNotice(null)}
          sx={{ width: "100%", fontFamily: "Mulish" }}
          action={
            overviewNotice?.url ? (
              <Button
                color="inherit"
                size="small"
                href={overviewNotice.url}
                sx={{ fontFamily: "Mulish", fontWeight: 700 }}
              >
                Download
              </Button>
            ) : undefined
          }
        >
          {overviewNotice?.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};
